<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { subDays } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import ReportsAPI from 'dashboard/api/reports';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import ReportHeader from './ReportHeader.vue';
import ReportMetricCard from './ReportMetricCard.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const inboxId = ref(null);
const assigneeId = ref(null);
const isLoading = ref(false);
const hasError = ref(false);
const report = ref({ totals: {}, by_day: {} });

// MOD-9 (CSV+orden): estado de ordenamiento de la tabla (client-side).
const sortKey = ref('day');
const sortOrder = ref('desc');

const since = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const until = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const CATEGORIES = [
  'total',
  'customer',
  'agent',
  'automation',
  'follow_up',
  'unknown',
];

const num = value => Number(value || 0);

const totals = computed(() => report.value.totals || {});

// MOD-9 (CSV+orden): ordena por columna seleccionada. 'day' compara la fecha ISO
// (YYYY-MM-DD ordena cronológicamente); las categorías comparan numéricamente.
const dailyRows = computed(() => {
  const byDay = report.value.by_day || {};
  const rows = Object.keys(byDay).map(day => ({ day, ...byDay[day] }));
  const key = sortKey.value;
  const dir = sortOrder.value === 'asc' ? 1 : -1;
  return rows.sort((a, b) => {
    if (key === 'day') {
      if (a.day < b.day) return -dir;
      if (a.day > b.day) return dir;
      return 0;
    }
    return (num(a[key]) - num(b[key])) * dir;
  });
});

// MOD-9 (CSV+orden): click en encabezado. Misma columna alterna asc/desc; otra la activa en desc.
const setSort = key => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'desc';
  }
};

const sortIndicator = key => {
  if (sortKey.value !== key) return '';
  return sortOrder.value === 'asc' ? ' ▲' : ' ▼';
};

const formatDay = day => {
  const [year, month, date] = day.split('-');
  return new Date(year, Number(month) - 1, date).toLocaleDateString('es', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// MOD-9 (CSV+orden): descarga la tabla tal como se ve (orden actual) + fila de totales.
// Client-side sobre `report` ya cargado, por lo que respeta los filtros activos.
const escapeCsv = value => `"${String(value ?? '').replace(/"/g, '""')}"`;

const downloadReport = () => {
  if (!dailyRows.value.length) return;

  const header = [
    t('REPORT.CONVERSATION_ORIGIN.TABLE.DAY'),
    ...CATEGORIES.map(category =>
      t(`REPORT.CONVERSATION_ORIGIN.CATEGORIES.${category}`)
    ),
  ];

  const bodyRows = dailyRows.value.map(row => [
    formatDay(row.day),
    ...CATEGORIES.map(category => num(row[category])),
  ]);

  const totalsRow = [
    t('REPORT.CONVERSATION_ORIGIN.TABLE.TOTALS_ROW'),
    ...CATEGORIES.map(category => num(totals.value[category])),
  ];

  const csv = [header, ...bodyRows, totalsRow]
    .map(row => row.map(escapeCsv).join(','))
    .join('\n');

  downloadCsvFile(
    generateFileName({ type: 'conversation_origin', to: until.value }),
    csv
  );
};

let requestId = 0;
const fetchReport = async () => {
  requestId += 1;
  const currentRequest = requestId;
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await ReportsAPI.getConversationOrigin({
      since: since.value,
      until: until.value,
      inboxId: inboxId.value || undefined,
      assigneeId: assigneeId.value || undefined,
    });
    if (currentRequest !== requestId) return;
    report.value = {
      totals: data?.totals || {},
      by_day: data?.by_day || {},
      timezone: data?.timezone,
    };
  } catch (error) {
    if (currentRequest !== requestId) return;
    hasError.value = true;
    if (currentRequest !== requestId) return;
    report.value = { totals: {}, by_day: {} };
  } finally {
    if (currentRequest === requestId) isLoading.value = false;
  }
};

const onDateRangeChange = ([startDate, endDate, rangeType]) => {
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchReport();
};

const onInboxChange = () => {
  fetchReport();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  fetchReport();
});
</script>

<template>
  <ReportHeader :header-title="$t('REPORT.CONVERSATION_ORIGIN.TITLE')">
    <button
      type="button"
      class="flex items-center h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 disabled:opacity-50 disabled:cursor-not-allowed"
      :disabled="isLoading || !dailyRows.length"
      @click="downloadReport"
    >
      {{ $t('REPORT.CONVERSATION_ORIGIN.DOWNLOAD') }}
    </button>
  </ReportHeader>
  <p class="px-4 text-xs text-n-slate-11">{{ report.timezone }}</p>
  <div class="flex flex-col flex-1 gap-6">
    <div class="flex flex-col flex-wrap w-full gap-3 md:flex-row">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <select
        v-model="inboxId"
        class="w-full md:w-60 mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
        @change="onInboxChange"
      >
        <option :value="null">
          {{ $t('REPORT.CONVERSATION_ORIGIN.ALL_INBOXES') }}
        </option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
      <select
        v-model="assigneeId"
        class="w-full md:w-60 mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
        @change="fetchReport"
      >
        <option :value="null">
          {{ $t('REPORT.CONVERSATION_ORIGIN.ALL_AGENTS') }}
        </option>
        <option value="unassigned">
          {{ $t('REPORT.CONVERSATION_ORIGIN.UNASSIGNED') }}
        </option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
    </div>

    <div
      v-if="hasError"
      class="p-4 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
    >
      {{ $t('REPORT.CONVERSATION_ORIGIN.ERROR') }}
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-4 md:grid-cols-5">
        <ReportMetricCard
          v-for="category in CATEGORIES"
          :key="category"
          :label="$t(`REPORT.CONVERSATION_ORIGIN.CATEGORIES.${category}`)"
          :value="String(num(totals[category]))"
          :info-text="$t(`REPORT.CONVERSATION_ORIGIN.LEGEND.${category}`)"
        />
      </div>

      <div class="overflow-x-auto border rounded-lg border-n-weak">
        <table class="w-full text-sm text-left">
          <thead class="text-n-slate-11 bg-n-alpha-black2">
            <tr>
              <th
                class="px-4 py-3 font-medium cursor-pointer select-none"
                @click="setSort('day')"
              >
                {{ $t('REPORT.CONVERSATION_ORIGIN.TABLE.DAY')
                }}{{ sortIndicator('day') }}
              </th>
              <th
                v-for="category in CATEGORIES"
                :key="category"
                class="px-4 py-3 font-medium text-right cursor-pointer select-none"
                @click="setSort(category)"
              >
                {{ $t(`REPORT.CONVERSATION_ORIGIN.CATEGORIES.${category}`)
                }}{{ sortIndicator(category) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-if="!dailyRows.length">
              <td
                :colspan="CATEGORIES.length + 1"
                class="px-4 py-6 text-center text-n-slate-11"
              >
                {{ $t('REPORT.CONVERSATION_ORIGIN.TABLE.NO_DATA') }}
              </td>
            </tr>
            <tr v-for="row in dailyRows" :key="row.day" class="text-n-slate-12">
              <td class="px-4 py-3 whitespace-nowrap">
                {{ formatDay(row.day) }}
              </td>
              <td
                v-for="category in CATEGORIES"
                :key="category"
                class="px-4 py-3 text-right tabular-nums"
              >
                {{ num(row[category]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak">
        <h3 class="m-0 text-sm font-medium text-n-slate-12">
          {{ $t('REPORT.CONVERSATION_ORIGIN.LEGEND.HEADING') }}
        </h3>
        <ul class="flex flex-col gap-1 m-0 text-sm text-n-slate-11">
          <li v-for="category in CATEGORIES" :key="category">
            <span class="font-medium text-n-slate-12">
              {{ $t(`REPORT.CONVERSATION_ORIGIN.CATEGORIES.${category}`) }}:
            </span>
            {{ $t(`REPORT.CONVERSATION_ORIGIN.LEGEND.${category}`) }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
